import React, { useState, useEffect, useCallback } from 'react';
import { votesApi } from '../../api';
import HrnbButton from '../controls/HrnbButton';
import HrnbLoader from '../controls/HrnbLoader';

/**
 * Управление сессией голосования раунда (для владельца баттла / ADMIN).
 * Показывает статус сессии и кнопку «Открыть/Закрыть голосование».
 *
 * Эндпоинты VoteController:
 * - GET  /battles/{battleId}/rounds/{roundId}/session
 * - POST /battles/{battleId}/rounds/{roundId}/session/open
 * - POST /battles/{battleId}/rounds/{roundId}/session/close
 *
 * @component
 * @param {string} props.battleId - UUID баттла
 * @param {string} props.roundId - UUID раунда
 * @param {boolean} props.disabled - Заблокировать кнопки (например, раунд завершён)
 * @param {Function} props.onSessionChange - Callback (status) после изменения
 */
export default function RoundSessionControl({ battleId, roundId, disabled = false, onSessionChange }) {
  const [session, setSession] = useState(null); // null = не загружено, undefined = нет сессии
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSession = useCallback(async () => {
    if (!battleId || !roundId) return;
    setLoading(true);
    try {
      const data = await votesApi.getSession(battleId, roundId);
      setSession(data);
    } catch (err) {
      // 404 — сессия ещё не создавалась
      setSession(undefined);
    } finally {
      setLoading(false);
    }
  }, [battleId, roundId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const handleToggle = async () => {
    const isOpen = session?.status === 'OPEN';
    const confirmed = isOpen
      ? window.confirm('Закрыть сессию голосования? Жюри и зрители больше не смогут выставлять оценки.')
      : window.confirm('Открыть сессию голосования для этого раунда?');
    if (!confirmed) return;

    setActionLoading(true);
    setError(null);
    try {
      const updated = isOpen
        ? await votesApi.closeSession(battleId, roundId)
        : await votesApi.openSession(battleId, roundId);
      setSession(updated);
      onSessionChange?.(updated?.status);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось изменить сессию голосования');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <HrnbLoader size="sm" />;
  }

  const isOpen = session?.status === 'OPEN';
  const isClosed = session?.status === 'CLOSED';

  return (
    <span className="round-session-control">
      {error && (
        <span className="round-session-control__error" title={error}>⚠️</span>
      )}
      {isClosed && (
        <span className="round-session-control__status round-session-control__status--closed">
          🔒 Голосование закрыто
        </span>
      )}
      {isOpen && (
        <span className="round-session-control__status round-session-control__status--open">
          🗳️ Голосование открыто
        </span>
      )}
      <HrnbButton
        variant={isOpen ? 'outline-danger' : 'outline'}
        size="sm"
        onClick={handleToggle}
        disabled={disabled || actionLoading}
        title={isOpen ? 'Закрыть сессию голосования' : 'Открыть сессию голосования'}
      >
        {actionLoading
          ? <HrnbLoader size="sm" />
          : isOpen ? 'Закрыть голосование' : 'Открыть голосование'}
      </HrnbButton>
    </span>
  );
}
